import { useMemo } from "react";
import { useIntl } from "react-intl";

import { getArrayMap, getUniqueArray } from "@citrine/utils/helpers";

import type { DataSourceID, TrainingData } from "../data-source";
import {
  getDataIdentifier,
  isSnapshot,
  isTableData,
  parseDataIdentifier,
} from "../data-source";
import type { IDescriptor, IRealDescriptor } from "../descriptor";
import {
  descriptorComparator,
  getDescriptorKey,
  transformDescriptor,
} from "../descriptor";
import type { DesignWorkflow } from "../design-workflow";
import type { IGEMTable } from "../gem-table";
import type { PredictorEvaluator } from "../predictor-evaluation-workflow";
import {
  DEFAULT_COVERAGE_LEVEL,
  DEFAULT_N_FOLDS,
  DEFAULT_N_TRIALS,
  EVALUATOR_METRIC_TYPES,
} from "../predictor-evaluation-workflow";
import type { PredictorSummary } from "../predictor-summary";
import { PredictorSummaryClient } from "../predictor-summary";
import type { ExplicitArrayMap } from "../utils";
import type { DataSourceIdentifier } from "../vquery/types";

import type {
  IGraphMLPredictorConfig,
  IPredictorConfig,
  PredictorConfigMap,
  PredictorEntity,
  ScalarPredictorConfig,
} from "./predictor";
import {
  isAutoMLPredictorConfig,
  isIngredientsToSimpleMixturePredictorConfig,
} from "./typeguards";

export const AVERAGE_RETRAIN_TIME = {
  lower: "10",
  upper: "18",
};

export function getEntityGraphSubPredictorsMap(predictor: PredictorEntity) {
  return getArrayMap(
    predictor.data.instance.predictors,
    (config) => config.type,
  ) as Partial<ExplicitArrayMap<ScalarPredictorConfig>>;
}

export function useTrainingData(projectId: string, workspace?: DesignWorkflow) {
  const { predictor_id, data_source_id } = workspace ?? {};

  const predictorSummaryQuery = PredictorSummaryClient.getInstance(
    projectId,
  ).useGet(predictor_id ?? undefined);

  return useMemo(
    () =>
      getTrainingData(data_source_id ?? undefined, predictorSummaryQuery.data),
    [data_source_id, predictorSummaryQuery.data],
  );
}

export function getTrainingData(
  dataSourceId?: DataSourceID,
  predictor?: PredictorEntity | PredictorSummary,
) {
  const parsedIdentifier = dataSourceId
    ? parseDataIdentifier(dataSourceId)
    : undefined;

  if (parsedIdentifier) {
    return [parsedIdentifier];
  }

  if (predictor) {
    return getTrainingDataFromPredictor(predictor);
  }
}

export function useTrainingDataDescription(
  trainingData?: TrainingData[],
  tableMap?: Partial<Record<string, IGEMTable>>,
) {
  const { formatMessage } = useIntl();

  // a query likely has not loaded
  if (!tableMap || !trainingData) {
    return { name: undefined, version: undefined };
  }

  if (trainingData.length === 1) {
    const dataSource = trainingData[0];
    // Single gem table sources shows table description
    if (isTableData(dataSource)) {
      const gemTable = tableMap[dataSource.table_id];

      if (gemTable?.table_config.name) {
        return {
          name: gemTable.table_config.name,
          version: dataSource.table_version,
        };
      }
    }
  }

  return {
    name: formatMessage(
      {
        id: "{count} data sources",
      },
      { count: trainingData.length },
    ),
    version: undefined,
  };
}

export function predictorEntityHasIngredientsToSimpleMixture(
  model?: PredictorEntity,
) {
  return model?.data.instance.predictors.some(
    isIngredientsToSimpleMixturePredictorConfig,
  );
}

function getDefaultEvaluatorReponsesForConfig(
  predictorConfig?: IPredictorConfig,
) {
  switch (predictorConfig?.type) {
    case "AnalyticExpression":
    case "Expression":
      return [predictorConfig.output.descriptor_key];
    case "AutoML":
      return predictorConfig.outputs.map(getDescriptorKey);
    case "Simple":
      return [
        ...predictorConfig.latent_variables,
        ...predictorConfig.outputs,
      ].map(getDescriptorKey);
  }
  return [];
}

function getDefaultEvaluatorResponsesForPredictor(predictor?: PredictorEntity) {
  return (
    predictor?.data.instance.predictors.flatMap(
      getDefaultEvaluatorReponsesForConfig,
    ) ?? []
  );
}

export function getDefaultPredictorEvaluatorForPredictor(
  predictor?: PredictorEntity,
): PredictorEvaluator {
  return {
    type: "CrossValidationEvaluator",
    name: "Default Cross Validation Evaluator",
    description: "Default Cross Validation Evaluator",
    responses: getDefaultEvaluatorResponsesForPredictor(predictor),
    n_folds: DEFAULT_N_FOLDS,
    n_trials: DEFAULT_N_TRIALS,
    metrics: EVALUATOR_METRIC_TYPES.map((metric) =>
      metric === "CoverageProbability"
        ? {
            type: metric,
            coverage_level: DEFAULT_COVERAGE_LEVEL,
          }
        : {
            type: metric,
          },
    ),
    ignore_when_grouping: [],
  };
}

/**
 * Takes a Predictor and returns its descriptors
 */
export const getDescriptorsFromPredictor = (predictor: PredictorEntity) =>
  descriptorsFromConfig(predictor.data.instance);

type PredictorProcessors<Return> = {
  [Type in ScalarPredictorConfig["type"]]?: (
    config: PredictorConfigMap[Type],
  ) => Return[];
};

/**
 * A generic predictor traverser. This supports all predictor types and automatic
 * recursion into a Graph predictor's internal predictors.
 *
 * Define a processor configuration for the non-recursive predictors, and the graph
 * traversal code will use them. The code is strictly typed, so it will fail
 * upon adding any new predictor types, or upon failing to update any code that
 * calls processPredictorConfig. It has fall-through to ignoring unknown, new
 * predictor types for future-proofing.
 *
 * @param config
 * @param processors
 */
export const processPredictorConfig = <Return>(
  config: IPredictorConfig,
  processors: PredictorProcessors<Return>,
): Return[] => {
  // "type": "Graph" is optional
  if (!config.type || config.type === "Graph") {
    return config.predictors.flatMap((p) =>
      processPredictorConfig<Return>(p, processors),
    );
  }

  // typescript is having trouble figuring out the config type here, but this is a valid call
  return processors[config.type]?.(config as any) ?? [];
};

/**
 * Takes a Predictor config and returns its descriptors
 */
const descriptorsFromConfig = (predictorConfig: IPredictorConfig) => {
  // descriptors in child-predictors may be duplicated
  return getUniqueArray(
    processPredictorConfig<IDescriptor>(predictorConfig, {
      AutoML: (config) => [...config.inputs, ...config.outputs],
      Simple: (config) => [
        ...config.inputs,
        ...config.latent_variables,
        ...config.outputs,
      ],
      Expression: (config) => [config.output],
      AnalyticExpression: (config) => [
        config.output,
        // TODO: remove this when https://citrine.atlassian.net/browse/PLA-5408 is implemented
        ...(Object.values(config.aliases) as IDescriptor[]),
      ],
      IngredientFractions: (config) => [config.input],
      IngredientsToSimpleMixture: (config) => [config.output],
      GeneralizedMeanProperty: (config) => [config.input],
      MeanProperty: (config) => [config.input, ...config.properties],
      LabelFractions: (config) => [config.input],
      SimpleMixture: (config) => [config.input, config.output],
      MoleculeFeaturizer: (config) => [config.descriptor],
      ChemicalFormulaFeaturizer: (config) => [config.input],
      AttributeAccumulation: () => [],
    }),
    getDescriptorKey,
  ).sort(descriptorComparator);
};

export const getPredictorOutputs = (
  predictor: PredictorEntity,
): IDescriptor[] => {
  return getOutputsFromConfig(predictor.data.instance);
};

export const getOutputsFromConfig = (
  predictorConfig: IPredictorConfig,
): IDescriptor[] => {
  return processPredictorConfig<IDescriptor>(predictorConfig, {
    AutoML: (config) => config.outputs,
    Simple: (config) => config.outputs,
    Expression: (config) => [config.output],
    AnalyticExpression: (config) => [config.output],
    IngredientsToSimpleMixture: (config) => [config.output],
    SimpleMixture: (config) => [config.output],
  });
};

export const getAutoMlInputsFromGraphConfig = (
  predictorConfig: IGraphMLPredictorConfig,
): IDescriptor[] => {
  return predictorConfig.predictors
    .filter(isAutoMLPredictorConfig)
    .flatMap(({ inputs }) => inputs);
};

export const getInputsFromConfig = (
  predictorConfig: IPredictorConfig,
): IDescriptor[] => {
  return processPredictorConfig<IDescriptor>(predictorConfig, {
    AutoML: (config) => config.inputs,
    Simple: (config) => config.inputs,
    IngredientFractions: (config) => [config.input],
    GeneralizedMeanProperty: (config) => [config.input],
    LabelFractions: (config) => [config.input],
    MeanProperty: (config) => [config.input],
    SimpleMixture: (config) => [config.input],
    MoleculeFeaturizer: (config) => [config.descriptor],
    ChemicalFormulaFeaturizer: (config) => [config.input],
  });
};

export const getPropertiesFromConfig = (
  predictorConfig: IPredictorConfig,
): IDescriptor[] => {
  return processPredictorConfig<IDescriptor>(predictorConfig, {
    MeanProperty: (config) => config.properties,
  });
};

export const getLatentVariablesFromConfig = (
  predictorConfig: IPredictorConfig,
): IDescriptor[] => {
  return processPredictorConfig<IDescriptor>(predictorConfig, {
    Simple: (config) => config.latent_variables,
  });
};

/**
 * The below transform functions are necessary due to legacy descriptor types
 * see: ../descriptor/Descriptor.ts for more
 *
 * These transform functions mutate the original object in case of Symbol or
 * hidden props we do not want to lose (there are, trust me)
 */
export function transformTrainingData(td?: TrainingData): void {
  /**
   * td is returned from case statements to ensure we update if/when a new
   * type is added
   */
  switch (td?.type) {
    case "csv_data_source":
      Object.values(td.column_definitions).forEach((d: IDescriptor) =>
        transformDescriptor(d),
      );
      return;
  }
}

export function transformPredictorConfig(config: IPredictorConfig): void {
  switch (config.type) {
    case "AttributeAccumulation":
      return;
    case "AnalyticExpression":
      transformDescriptor(config.output);
      return;

    case "AutoML":
      config.inputs.forEach(transformDescriptor);
      if (!config.outputs) {
        config.outputs = (config as any).responses;
      }
      config.outputs.forEach(transformDescriptor);
      return;

    case "ChemicalFormulaFeaturizer":
      transformDescriptor(config.input);
      return;

    case "Expression":
      transformDescriptor(config.output);
      return;

    case "GeneralizedMeanProperty":
      transformDescriptor(config.input);
      return;

    case "IngredientFractions":
      transformDescriptor(config.input);
      return;

    case "IngredientsToSimpleMixture":
      transformDescriptor(config.output);
      (Object.values(config.id_to_quantity) as IRealDescriptor[]).forEach(
        transformDescriptor,
      );
      return;

    case "LabelFractions":
      transformDescriptor(config.input);
      return;

    case "MeanProperty":
      transformDescriptor(config.input);
      config.properties.forEach(transformDescriptor);
      return;

    case "MoleculeFeaturizer":
      transformDescriptor(config.descriptor);
      return;

    case "Simple":
      config.inputs.forEach(transformDescriptor);
      config.latent_variables.forEach(transformDescriptor);
      config.outputs.forEach(transformDescriptor);
      return;

    case "SimpleMixture":
      transformDescriptor(config.input);
      transformDescriptor(config.output);
      return;
  }

  config.predictors.forEach(transformPredictorConfig);
  config.training_data?.forEach(transformTrainingData);
}

export function getTrainingDataFromPredictor(
  predictor?: PredictorEntity | PredictorSummary,
): TrainingData[] {
  if (predictor) {
    const trainingData =
      "instance" in predictor.data
        ? predictor.data.instance.training_data
        : predictor.data.training_data;
    return getUniqueArray(trainingData ?? [], getDataIdentifier);
  }
  return [];
}

export function parseFilter(filters: string | string[]) {
  const filterArray =
    typeof filters === "string" ? filters.split(",") : filters;

  return (filterArray ?? []).reduce(
    (acc, filter) => {
      const [key, value] = filter.split(" eq ");
      if (value !== undefined) {
        acc[key] = value;
      }
      return acc;
    },
    {} as Partial<Record<string, string>>,
  );
}

export function getVQueryableBackingTrainingData(
  predictorEntity: PredictorEntity,
): DataSourceIdentifier | undefined {
  const trainingData = getTrainingDataFromPredictor(predictorEntity);
  if (trainingData.length !== 1) {
    return undefined;
  }
  const orionDataSource = trainingData[0];
  if (isSnapshot(orionDataSource)) {
    return {
      type: "snapshot",
      id: orionDataSource.snapshot_id,
    };
  }
  if (isTableData(orionDataSource)) {
    return {
      type: "gem_table",
      id: orionDataSource.table_id,
      version: orionDataSource.table_version,
    };
  }
}
